import { kickHeights, teamAColor, teamBColor, useBuilderContext } from "@Context/game/builder/builder-context";
import ScenarioActionType from "@Enums/ScenarioActionType";
import BootKick from "@Resources/game/builder/boot-kick.png";
import Compass from "@Resources/game/builder/compass.png";
import Dot from "@Resources/game/builder/dot.png";
import Dot2 from "@Resources/game/builder/dot2.png";
import Dot3 from "@Resources/game/builder/dot3.png";
import EyeTarget from "@Resources/game/builder/eye-target.png";
import Forward from "@Resources/game/builder/forward.png";
import RaisedHand from "@Resources/game/builder/raised-hand.png";
import Score from "@Resources/game/builder/score.png";
import Ball from "@Resources/game/builder/soccer-ball.png";
import Target from "@Resources/game/builder/targeted.png";
import { Point } from "@Shared/types/ScenarioTypes";
import "@pixi/events";
import { Graphics as PixiGraphics } from "@pixi/graphics";
import { Graphics, Sprite, Text } from "@pixi/react";
import { useCallback, useEffect, useState } from "react";
import { getCategoryForHeight, getHeightFromFlightTime, getNearestKickHeight } from "./functions/physics";
import { getPlayer, updateAction } from "./functions/player-functions";
import { getPointAtTime, getTimeAtPoint, stageToScreen } from "./functions/utilities";
import { PhysicsPrompt, Player, PlayerAction as PlayerActionType } from "./models/builder-data-types";

const actionIconMap = new Map<ScenarioActionType, string>([
  [ScenarioActionType.GetBall, Ball],
  [ScenarioActionType.PassTarget, BootKick],
  [ScenarioActionType.ShootDirection, Target],
  [ScenarioActionType.LookTarget, EyeTarget],
  [ScenarioActionType.FaceTarget, Compass],
  [ScenarioActionType.FaceLocation, Compass],
  [ScenarioActionType.Face, Forward],
  [ScenarioActionType.Signal, RaisedHand],
  [ScenarioActionType.Score, Score],
]);

const PlayerAction = (props: { action: PlayerActionType; actionIndex: number; playerId: number; size: number }) => {
  const { state, dispatch } = useBuilderContext();
  const [position, setPosition] = useState<Point>({ x: 0, y: 0 });
  const [endPosition, setEndPosition] = useState<Point>({ x: 0, y: 0 });
  const [icon, setIcon] = useState<string>("");
  const [time, setTime] = useState(props.action.startTime);
  const [isDragging, setIsDragging] = useState(false);
  const [playerId, setPlayerId] = useState(props.playerId);
  const [actionIndex, setActionIndex] = useState(props.actionIndex);
  const [player, setPlayer] = useState<Player | undefined>(getPlayer(state.clip, props.playerId));
  const [isSelected, setIsSelected] = useState(state.selectedAction === props.actionIndex);
  const [endTime, setEndTime] = useState(props.action.endTime);
  const [showTargetHandle, setShowTargetHandle] = useState(false);
  const [targetPosition, setTargetPosition] = useState<Point>(props.action.targetPosition);
  const [targetPlayer, setTargetPlayer] = useState<Player | undefined>(state.clip.scenario.players.get(props.action.targetPlayer));
  const [heightName, setHeightName] = useState("");
  const [heightPosition, setHeightPosition] = useState<Point>({ x: 0, y: 0 });
  const [showTargetLine, setShowTargetLine] = useState(false);

  useEffect(() => {
    setIcon(actionIconMap.get(props.action.type) || "");
    setTime(props.action.startTime);
    setEndTime(props.action.endTime);

    const targetPlayer = state.clip.scenario.players.get(props.action.targetPlayer);
    setTargetPosition(
      targetPlayer ? getPointAtTime(targetPlayer.points, props.action.time, targetPlayer.beziers).position : props.action.targetPosition
    );
    setTargetPlayer(targetPlayer);

    if (props.action.type === ScenarioActionType.PassTarget || props.action.type === ScenarioActionType.ShootDirection) {
      let heightDiff = 99999;
      let heightText = "";
      kickHeights.forEach((height, index) => {
        const diff = Math.abs(height.height - props.action.height);
        if (diff < heightDiff) {
          heightDiff = diff;
          heightText = height.name;
        }
      });

      setHeightName(heightText);
    }

    setShowTargetHandle(
      props.action.type === ScenarioActionType.FaceLocation ||
        props.action.type === ScenarioActionType.ShootDirection ||
        props.action.type === ScenarioActionType.PassTarget
    );
  }, [props.action, state.clip.scenario.players]);

  useEffect(() => {
    setShowTargetLine(
      (targetPlayer || targetPosition) &&
        (props.action.type === ScenarioActionType.FaceLocation ||
          props.action.type === ScenarioActionType.FaceTarget ||
          props.action.type === ScenarioActionType.LookTarget)
    );
  }, [props.action.type, targetPlayer, targetPosition]);

  useEffect(() => {
    if (!position || !targetPosition) return;
    setHeightPosition({
      x: (position.x + targetPosition.x) / 2,
      y: (position.y + targetPosition.y) / 2,
    });
  }, [position, targetPosition]);

  useEffect(() => {
    setIsSelected(state.selectedAction === props.actionIndex);
  }, [state.selectedAction, props.actionIndex]);

  const handleClick = () => {
    if (state.selectedAction === props.actionIndex && !isDragging) {
      dispatch({
        radialAnchor: stageToScreen(position, state),
        selectedWaypoint: -1,
        showActionRadial: true,
        showPlayerRadial: false,
        showFieldRadial: false,
        showWaypointRadial: false,
      });
    } else {
      dispatch({
        selectedAction: props.actionIndex,
        selectedWaypoint: -1,
      });
    }
  };

  const handleDragStart = (event: any) => {
    event.currentTarget.alpha = 0.5;
    event.currentTarget.dragData = event.data;
    event.currentTarget.dragging = true;
  };

  const handleDragMove = (event: any) => {
    if (player && event.currentTarget.dragging) {
      const dragPos = event.currentTarget.dragData.getLocalPosition(event.currentTarget.parent);
      const newTime = getTimeAtPoint(player.points, dragPos, player.beziers);
      const newPosition = getPointAtTime(player.points, newTime, player.beziers).position;
      setTime(newTime);
      setPosition(newPosition);
      setIsDragging(true);
    }
  };

  const isPassOrShoot = (type: number) =>
    type === ScenarioActionType.PassTarget || type === ScenarioActionType.ShootDirection;

  const validatePassPhysics = (newAction: PlayerActionType, onCancel: () => void): boolean => {
    if (!isPassOrShoot(newAction.type) || newAction.height <= 0) return true;

    const newFlightTime = newAction.time - newAction.startTime;
    const category = getCategoryForHeight(newAction.height);
    const naturalHeight = getHeightFromFlightTime(newFlightTime);

    if (naturalHeight >= category.min && naturalHeight <= category.max) {
      newAction.height = naturalHeight;
      return true;
    }

    const nearestHeight = getNearestKickHeight(naturalHeight);
    const currentHeightInfo = getCategoryForHeight(newAction.height);

    dispatch({
      selectedWaypoint: -1,
      physicsPrompt: {
        mode: "time-changed",
        playerId,
        actionIndex,
        currentHeight: newAction.height,
        currentFlightTime: newFlightTime,
        proposedHeight: nearestHeight.height,
        proposedFlightTime: newFlightTime,
        proposedHeightName: nearestHeight.name,
        currentHeightName: currentHeightInfo.name,
        onConfirm: () => {
          const adjusted = { ...newAction, height: nearestHeight.height };
          updateAction(state, dispatch, playerId, actionIndex, adjusted);
        },
        onCancel,
      },
    });
    return false;
  };

  const handleDragEnd = (event: any) => {
    event.currentTarget.alpha = 1;
    event.currentTarget.dragging = false;
    event.currentTarget.dragData = null;
    setIsDragging(false);

    if (!player) return;
    const newAction = { ...props.action };
    newAction.startTime = time;
    newAction.endTime = Math.max(newAction.startTime, newAction.endTime);
    if (isPassOrShoot(newAction.type)) {
      newAction.time = Math.max(newAction.time, newAction.startTime + 0.075);
    }

    const originalTime = props.action.startTime;
    const originalPosition = getPointAtTime(player.points, originalTime, player.beziers).position;
    if (!validatePassPhysics(newAction, () => {
      setTime(originalTime);
      setPosition(originalPosition);
    })) return;

    updateAction(state, dispatch, playerId, actionIndex, newAction);
    dispatch({ selectedWaypoint: -1 });
  };

  const handleEndPointDragStart = (event: any) => {
    event.currentTarget.alpha = 0.5;
    event.currentTarget.dragData = event.data;
    event.currentTarget.dragging = true;
    setIsDragging(true);
  };

  const handleEndPointDragMove = (event: any) => {
    if (player && event.currentTarget.dragging) {
      const dragPos = event.currentTarget.dragData.getLocalPosition(event.currentTarget.parent);
      const newTime = getTimeAtPoint(player.points, dragPos, player.beziers);
      const newPosition = getPointAtTime(player.points, newTime, player.beziers).position;
      setEndTime(newTime);
      setEndPosition(newPosition);
      setIsDragging(true);
    }
  };

  const handleEndPointDragEnd = (event: any) => {
    event.currentTarget.alpha = 1;
    event.currentTarget.dragging = false;
    event.currentTarget.dragData = null;

    if (!player) return;
    const newAction = { ...props.action };
    newAction.endTime = getTimeAtPoint(player.points, endPosition, player.beziers);
    const startTimeClamped = newAction.endTime < newAction.startTime;
    newAction.startTime = Math.min(newAction.startTime, newAction.endTime);

    if (startTimeClamped) {
      const originalEndTime = props.action.endTime;
      const originalEndPosition = getPointAtTime(player.points, originalEndTime, player.beziers).position;
      if (!validatePassPhysics(newAction, () => {
        setEndTime(originalEndTime);
        setEndPosition(originalEndPosition);
      })) {
        setIsDragging(false);
        return;
      }
    }

    updateAction(state, dispatch, playerId, actionIndex, newAction);
    setIsDragging(false);
    dispatch({ selectedWaypoint: -1 });
  };

  const handleTargetDragStart = (event: any) => {
    event.currentTarget.alpha = 0.5;
    event.currentTarget.dragData = event.data;
    event.currentTarget.dragging = true;
  };

  const handleTargetDragMove = (event: any) => {
    if (player && event.currentTarget.dragging) {
      const dragPos = event.currentTarget.dragData.getLocalPosition(event.currentTarget.parent);
      setTargetPosition(dragPos);
    }
  };

  const handleTargetDragEnd = (event: any) => {
    event.currentTarget.alpha = 1;
    event.currentTarget.dragging = false;
    event.currentTarget.dragData = null;

    if (!player) return;
    const newAction = { ...props.action };
    const endPos = { x: event.currentTarget.x, y: event.currentTarget.y };
    setTargetPosition(endPos);
    newAction.targetPosition = endPos;
    if (newAction.type === ScenarioActionType.PassTarget) {
      const tp = getPlayer(state.clip, props.action.targetPlayer);
      if (tp) {
        const newTime = getTimeAtPoint(tp.points, targetPosition, tp.beziers);
        newAction.time = Math.max(props.action.startTime + 0.075, newTime);
      }
    }

    const origTargetPlayer = state.clip.scenario.players.get(props.action.targetPlayer);
    const originalTargetPos = origTargetPlayer
      ? getPointAtTime(origTargetPlayer.points, props.action.time, origTargetPlayer.beziers).position
      : props.action.targetPosition;
    if (!validatePassPhysics(newAction, () => {
      setTargetPosition(originalTargetPos);
    })) return;

    updateAction(state, dispatch, playerId, actionIndex, newAction);
  };

  useEffect(() => {
    if (!player) return;
    setEndPosition(getPointAtTime(player.points, props.action.endTime, player.beziers).position);
  }, [state.clip.scenario.players, props.action.endTime, props.action.startTime]);

  useEffect(() => {
    setPlayerId(props.playerId);
    setActionIndex(props.actionIndex);
    const newPlayer = getPlayer(state.clip, props.playerId);
    setPlayer(newPlayer);
    if (newPlayer) {
      setPosition(getPointAtTime(newPlayer.points, newPlayer.actions[props.actionIndex].startTime, newPlayer.beziers).position);
      return;
    }
  }, [state.clip.scenario.players, props.playerId, props.actionIndex]);

  const drawPath = useCallback(
    (g: PixiGraphics) => {
      g.clear();
      if (!player) return;
      g.lineStyle(10 / state.mapScale, "white", 1);
      g.moveTo(position.x, position.y);
      const length = endTime - time;
      const dT = length / 50;
      for (let t = time + dT; t < endTime; t += dT) {
        const pos = getPointAtTime(player.points, t, player.beziers).position;
        g.lineTo(pos.x, pos.y);
      }

      g.lineTo(endPosition.x, endPosition.y);
    },
    [player, position, endPosition, time, endTime, state.mapScale]
  );

  const drawTargetPath = useCallback(
    (g: PixiGraphics) => {
      g.clear();
      if (!player) return;
      g.lineStyle(5 / state.mapScale, "white", 0.7);
      g.moveTo(position.x, position.y);
      if (targetPlayer) {
        const playerPos = getPointAtTime(targetPlayer.points, state.time, targetPlayer.beziers).position;
        g.lineTo(playerPos.x, playerPos.y);
      } else g.lineTo(targetPosition.x, targetPosition.y);
    },
    [position, targetPosition, targetPlayer, state.time, state.mapScale]
  );

  return (
    icon != "" && (
      <>
        {isSelected && heightName !== "" && (
          <Text
            eventMode="passive"
            text={heightName}
            anchor={0.5}
            x={heightPosition.x}
            y={heightPosition.y}
            rotation={-state.viewRotation}
            style={
              {
                align: "center",
                fill: "white",
                fontSize: 22 * state.spriteScale,
                fontWeight: "bold",
                letterSpacing: 0,
              } as any
            }
          />
        )}
        {isSelected && <Graphics draw={drawPath} />}
        {isSelected && showTargetLine && <Graphics draw={drawTargetPath} />}
        <Sprite
          image={Dot2}
          x={position.x}
          y={position.y}
          rotation={-state.viewRotation}
          width={props.size * 1.25 * state.spriteScale}
          height={props.size * 1.25 * state.spriteScale}
          anchor={0.5}
          alpha={1}
          tint={isSelected ? "white" : player?.team === 1 ? teamAColor : teamBColor}
          interactive={true}
          pointertap={handleClick}
          pointerdown={handleDragStart}
          pointerup={handleDragEnd}
          pointerupoutside={handleDragEnd}
          onglobalpointermove={handleDragMove}
        />
        <Sprite
          image={icon}
          x={position.x}
          y={position.y}
          rotation={-state.viewRotation}
          width={props.size * 0.75 * state.spriteScale}
          height={props.size * 0.75 * state.spriteScale}
          anchor={0.5}
          tint={isSelected ? "white" : player?.team === 1 ? teamAColor : teamBColor}
          interactive={true}
          pointertap={handleClick}
          pointerdown={handleDragStart}
          pointerup={handleDragEnd}
          pointerupoutside={handleDragEnd}
          onglobalpointermove={handleDragMove}
        />
        {isSelected && (
          <Sprite
            image={Dot}
            x={endPosition.x}
            y={endPosition.y}
            rotation={-state.viewRotation}
            width={props.size * 0.75 * state.spriteScale}
            height={props.size * 0.75 * state.spriteScale}
            anchor={0.5}
            tint={isSelected ? "white" : player?.team === 1 ? teamAColor : teamBColor}
            pointerdown={handleEndPointDragStart}
            pointerup={handleEndPointDragEnd}
            pointerupoutside={handleEndPointDragEnd}
            onglobalpointermove={handleEndPointDragMove}
            interactive={true}
          />
        )}
        {isSelected && showTargetHandle && (
          <Sprite
            image={Dot3}
            x={targetPosition.x}
            y={targetPosition.y}
            rotation={-state.viewRotation}
            width={props.size * 0.75 * state.spriteScale}
            height={props.size * 0.75 * state.spriteScale}
            anchor={0.5}
            tint={isSelected ? "white" : player?.team === 1 ? teamAColor : teamBColor}
            interactive={true}
            pointerdown={handleTargetDragStart}
            pointerup={handleTargetDragEnd}
            pointerupoutside={handleTargetDragEnd}
            onglobalpointermove={handleTargetDragMove}
          />
        )}
      </>
    )
  );
};

export default PlayerAction;
